/**
 * Global search — Cmd+K style search across messages, assets, entities.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import { Search, MessageSquare, Image, Database } from 'lucide-react';

export function GlobalSearchModal() {
  const { showSearch, setShowSearch, setActivityUserId, currentSeriesId, currentProjectId } = useAppStore();
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'all' | 'project'>('all');
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useQuery(
    api.search.globalSearch,
    q.length >= 2
      ? {
          q,
          accountId,
          projectId: scope === 'project' && currentSeriesId ? currentSeriesId : undefined,
          flyerProjectId: scope === 'project' && currentProjectId ? currentProjectId : undefined,
        }
      : 'skip'
  );

  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(!showSearch);
      }
      if (e.key === 'Escape') setShowSearch(false);
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [showSearch, setShowSearch]);

  useEffect(() => {
    if (showSearch) {
      inputRef.current?.focus();
      setQ('');
    }
  }, [showSearch]);

  if (!showSearch) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-background/80 backdrop-blur-sm"
      onClick={() => setShowSearch(false)}
    >
      <div
        className="w-full max-w-xl rounded-xl border border-border bg-card shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search messages, assets, entities…"
            className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none"
          />
          <kbd className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground hidden sm:inline">Esc</kbd>
        </div>
        {(currentSeriesId || currentProjectId) && (
          <div className="flex items-center gap-2 border-b border-border px-4 py-2">
            <span className="text-xs text-muted-foreground">Scope:</span>
            <button
              type="button"
              onClick={() => setScope('all')}
              className={`rounded px-2 py-1 text-xs ${scope === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted/50 hover:bg-muted'}`}
            >
              Everywhere
            </button>
            <button
              type="button"
              onClick={() => setScope('project')}
              className={`rounded px-2 py-1 text-xs ${scope === 'project' ? 'bg-primary text-primary-foreground' : 'bg-muted/50 hover:bg-muted'}`}
            >
              This project
            </button>
          </div>
        )}
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {q.length < 2 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Type at least 2 characters</p>
          ) : results ? (
            <div className="space-y-4">
              {results.messages.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-2 py-1 text-xs font-medium text-muted-foreground">
                    <MessageSquare className="h-3.5 w-3.5" /> Messages
                  </div>
                  <ul className="space-y-1">
                    {results.messages.map((m) => (
                      <li key={m.id} className="rounded-lg px-3 py-2 text-sm hover:bg-muted/50">
                        <span className="line-clamp-2">{m.content}</span>
                        <span className="text-xs text-muted-foreground">thread: {m.thread_id.slice(0, 8)}…</span>
                        {m.userId && (
                          <button
                            type="button"
                            onClick={() => {
                              setActivityUserId(m.userId!);
                              setShowSearch(false);
                            }}
                            className="block text-xs text-primary mt-1 hover:underline"
                          >
                            View user activity
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {results.assets.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-2 py-1 text-xs font-medium text-muted-foreground">
                    <Image className="h-3.5 w-3.5" /> Assets
                  </div>
                  <ul className="space-y-1">
                    {results.assets.map((a) => (
                      <li key={a.id} className="rounded-lg px-3 py-2 text-sm hover:bg-muted/50 cursor-pointer">
                        {a.name} <span className="text-muted-foreground">({a.type})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {results.entities.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-2 py-1 text-xs font-medium text-muted-foreground">
                    <Database className="h-3.5 w-3.5" /> Entities
                  </div>
                  <ul className="space-y-1">
                    {results.entities.map((e) => (
                      <li key={e.id} className="rounded-lg px-3 py-2 text-sm hover:bg-muted/50 cursor-pointer">
                        {e.name} <span className="text-muted-foreground">({e.entityType})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {results.messages.length === 0 && results.assets.length === 0 && results.entities.length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">No results</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">Searching…</p>
          )}
        </div>
      </div>
    </div>
  );
}
